<template>
  <b-modal
    :active.sync="showModal"
    trap-focus
    aria-role="dialog"
    scroll="keep"
    :width="900"
    aria-modal
    has-modal-card
    @close="closeModal"
  >
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head">
        <p class="modal-card-title">QC Result Details</p>
        <button class="delete" aria-label="close" @click="closeModal"></button>
      </header>

      <section class="modal-card-body">
        <!-- Loading State -->
        <div v-if="loading" class="has-text-centered py-5">
          <b-icon icon="loading" size="is-large" custom-class="mdi-spin"></b-icon>
          <p class="mt-3">Loading QC result...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="notification is-danger">
          <strong>Error loading QC result:</strong> {{ error }}
        </div>

        <!-- QC Result Content -->
        <div v-else-if="qcResult">
          <!-- Status Banner -->
          <div :class="['notification', statusClass]">
            <div class="columns is-vcentered">
              <div class="column">
                <strong class="is-size-5">
                  <b-icon :icon="statusIcon" size="is-small"></b-icon>
                  Status: {{ formatStatus(qcResult.qc_status) }}
                </strong>
                <p v-if="qcResult.source_filename" class="mt-2">
                  <b-icon icon="file-video" size="is-small"></b-icon>
                  {{ qcResult.source_filename }}
                </p>
              </div>
              <div class="column is-narrow">
                <b-button
                  v-if="qcResult.qc_status === 'passed' || qcResult.qc_status === 'passed_with_warnings'"
                  type="is-info"
                  size="is-small"
                  icon-left="file-pdf-box"
                  @click="downloadReport"
                >
                  Download Report
                </b-button>
              </div>
            </div>
          </div>

          <!-- Technical Specifications -->
          <div class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="cog" size="is-small"></b-icon>
              Technical Specifications
            </h4>
            <div class="columns is-multiline">
              <!-- Video Info -->
              <div class="column is-6">
                <h5 class="subtitle is-6 mb-2">Video</h5>
                <table class="table is-narrow is-fullwidth">
                  <tbody>
                    <tr>
                      <td><strong>Codec</strong></td>
                      <td>{{ qcResult.video_codec || 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td><strong>Resolution</strong></td>
                      <td>{{ qcResult.video_resolution || 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td><strong>Frame Rate</strong></td>
                      <td>{{ qcResult.video_framerate || 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td><strong>Scan Type</strong></td>
                      <td>{{ qcResult.video_scan_type || 'N/A' }}</td>
                    </tr>
                    <tr v-if="qcResult.video_bitrate_kbps">
                      <td><strong>Bitrate</strong></td>
                      <td>{{ (qcResult.video_bitrate_kbps / 1000).toFixed(1) }} Mbps</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Audio Info -->
              <div class="column is-6">
                <h5 class="subtitle is-6 mb-2">Audio</h5>
                <table class="table is-narrow is-fullwidth">
                  <tbody>
                    <tr>
                      <td><strong>{{ audioStreams.length > 1 ? 'Primary Stream' : 'Codec' }}</strong></td>
                      <td>{{ qcResult.audio_codec || 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td><strong>Channels</strong></td>
                      <td>{{ qcResult.audio_channels || 'N/A' }} ({{ qcResult.audio_layout || 'N/A' }})</td>
                    </tr>
                    <tr>
                      <td><strong>Sample Rate</strong></td>
                      <td>{{ qcResult.audio_sample_rate ? (qcResult.audio_sample_rate / 1000) + ' kHz' : 'N/A' }}</td>
                    </tr>
                    <tr v-if="qcResult.audio_converted">
                      <td><strong>Original Layout</strong></td>
                      <td>{{ qcResult.original_audio_layout || 'N/A' }}</td>
                    </tr>
                    <tr v-if="qcResult.audio_mapping_strategy || qcResult.audio_mapping_description">
                      <td><strong>Encode Mapping</strong></td>
                      <td>
                        <b-tag
                          :type="qcResult.audio_mapping_strategy === 'split-mono' ? 'is-info' : 'is-light'"
                          size="is-small"
                        >
                          {{ audioMappingLabel }}
                        </b-tag>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Source audio tracks: only show when there are multiple -->
                <div v-if="audioStreams.length > 1" class="mt-3">
                  <h6 class="subtitle is-6 mb-2">
                    Source audio tracks ({{ audioStreams.length }})
                  </h6>
                  <table class="table is-narrow is-fullwidth is-size-7">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Codec</th>
                        <th>Channels</th>
                        <th>Layout</th>
                        <th>Sample Rate</th>
                        <th>Bitrate</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(stream, idx) in audioStreams" :key="idx">
                        <td>{{ stream.index != null ? stream.index : idx }}</td>
                        <td>{{ stream.codec || 'N/A' }}</td>
                        <td>{{ stream.channels != null ? stream.channels + 'ch' : 'N/A' }}</td>
                        <td>{{ stream.channelLayout || '-' }}</td>
                        <td>{{ stream.sampleRate ? (stream.sampleRate / 1000) + ' kHz' : '-' }}</td>
                        <td>{{ stream.bitrate ? Math.round(stream.bitrate / 1000) + ' kbps' : '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Duration Analysis -->
          <div class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="timer" size="is-small"></b-icon>
              Duration Analysis
            </h4>
            <div class="columns">
              <div class="column">
                <table class="table is-narrow is-fullwidth">
                  <tbody>
                    <tr>
                      <td><strong>Detected Duration</strong></td>
                      <td>{{ formatDuration(qcResult.detected_duration_ms) }}</td>
                    </tr>
                    <tr>
                      <td><strong>Expected Duration</strong></td>
                      <td>{{ formatDuration(qcResult.expected_duration_ms) }}</td>
                    </tr>
                    <tr>
                      <td><strong>Difference</strong></td>
                      <td :class="{ 'has-text-danger': !qcResult.duration_match && qcResult.duration_diff_ms }">
                        {{ qcResult.duration_diff_ms ? (qcResult.duration_diff_ms / 1000).toFixed(2) + 's' : 'N/A' }}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Duration Match</strong></td>
                      <td>
                        <b-icon
                          :icon="qcResult.duration_match ? 'check-circle' : 'close-circle'"
                          :type="qcResult.duration_match ? 'is-success' : 'is-danger'"
                        ></b-icon>
                        {{ qcResult.duration_match ? 'Yes' : 'No' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Loudness Analysis -->
          <div class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="volume-high" size="is-small"></b-icon>
              Loudness Analysis (EBU R128)
            </h4>
            <div class="columns">
              <div class="column">
                <table class="table is-narrow is-fullwidth">
                  <tbody>
                    <tr>
                      <td><strong>Integrated Loudness</strong></td>
                      <td>{{ qcResult.loudness_i ? qcResult.loudness_i.toFixed(1) + ' LUFS' : 'N/A' }}</td>
                      <td class="has-text-grey">Target: -23 LUFS</td>
                    </tr>
                    <tr>
                      <td><strong>True Peak</strong></td>
                      <td :class="{ 'has-text-danger': qcResult.loudness_tp > -3 }">
                        {{ qcResult.loudness_tp ? qcResult.loudness_tp.toFixed(1) + ' dBTP' : 'N/A' }}
                      </td>
                      <td class="has-text-grey">Max: -3 dBTP</td>
                    </tr>
                    <tr>
                      <td><strong>Loudness Range</strong></td>
                      <td>{{ qcResult.loudness_lra ? qcResult.loudness_lra.toFixed(1) + ' LU' : 'N/A' }}</td>
                      <td class="has-text-grey">Max: 15 LU</td>
                    </tr>
                    <tr>
                      <td><strong>Compliant</strong></td>
                      <td colspan="2">
                        <b-icon
                          :icon="qcResult.loudness_compliant ? 'check-circle' : 'close-circle'"
                          :type="qcResult.loudness_compliant ? 'is-success' : 'is-danger'"
                        ></b-icon>
                        {{ qcResult.loudness_compliant ? 'Yes' : 'No' }}
                        <span v-if="qcResult.loudness_fixed" class="tag is-info is-light ml-2">Fixed</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Issues and Warnings -->
          <div v-if="qcResult.issues && qcResult.issues.length > 0" class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="alert-circle" size="is-small"></b-icon>
              Issues ({{ qcResult.issues.length }})
            </h4>
            <div class="content">
              <table class="table is-narrow is-fullwidth">
                <thead>
                  <tr>
                    <th style="width: 100px">Severity</th>
                    <th style="width: 120px">Type</th>
                    <th>Message</th>
                    <th style="width: 100px">Timecode</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(issue, index) in qcResult.issues" :key="index">
                    <td>
                      <span :class="['tag', severityClass(issue.severity)]">
                        {{ issue.severity || 'info' }}
                      </span>
                    </td>
                    <td>{{ issue.type || 'general' }}</td>
                    <td>{{ issue.message }}</td>
                    <td>{{ issue.timecode || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Black Frame Detection -->
          <div v-if="qcResult" class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="checkbox-blank" size="is-small"></b-icon>
              Black Frame Detection
            </h4>
            <div class="columns">
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Black Frames Found</p>
                  <p class="title is-4" :class="{ 'has-text-warning': qcResult.black_frame_count > 0 }">
                    {{ qcResult.black_frame_count || 0 }}
                  </p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Total Duration</p>
                  <p class="title is-4">{{ formatSeconds(qcResult.total_black_duration_s) }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Status</p>
                  <b-icon
                    :icon="qcResult.black_frame_count > 0 ? 'alert' : 'check-circle'"
                    :type="qcResult.black_frame_count > 0 ? 'is-warning' : 'is-success'"
                    size="is-medium"
                  ></b-icon>
                </div>
              </div>
            </div>
            <div v-if="blackFramesList && blackFramesList.length > 0" class="mt-4">
              <p class="subtitle is-6 mb-2">Detected Black Frames:</p>
              <div class="table-container" style="max-height: 200px; overflow-y: auto;">
                <table class="table is-narrow is-fullwidth is-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(frame, index) in blackFramesList" :key="'bf-' + index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatTimecode(frame.start_s || frame.black_start || frame.start) }}</td>
                      <td>{{ formatTimecode(frame.end_s || frame.black_end || frame.end) }}</td>
                      <td>{{ formatSeconds(frame.duration_s || frame.black_duration || frame.duration) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Silence Detection -->
          <div v-if="qcResult" class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="volume-off" size="is-small"></b-icon>
              Silence Detection
            </h4>
            <div class="columns">
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Silence Periods Found</p>
                  <p class="title is-4" :class="{ 'has-text-warning': qcResult.silence_count > 0 }">
                    {{ qcResult.silence_count || 0 }}
                  </p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Total Duration</p>
                  <p class="title is-4">{{ formatSeconds(qcResult.total_silence_duration_s) }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Status</p>
                  <b-icon
                    :icon="qcResult.silence_count > 0 ? 'alert' : 'check-circle'"
                    :type="qcResult.silence_count > 0 ? 'is-warning' : 'is-success'"
                    size="is-medium"
                  ></b-icon>
                </div>
              </div>
            </div>
            <div v-if="silencePeriodsList && silencePeriodsList.length > 0" class="mt-4">
              <p class="subtitle is-6 mb-2">Detected Silence Periods:</p>
              <div class="table-container" style="max-height: 200px; overflow-y: auto;">
                <table class="table is-narrow is-fullwidth is-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(period, index) in silencePeriodsList" :key="'sp-' + index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatTimecode(period.start_s || period.silence_start || period.start) }}</td>
                      <td>{{ formatTimecode(period.end_s || period.silence_end || period.end) }}</td>
                      <td>{{ formatSeconds(period.duration_s || period.silence_duration || period.duration) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Freeze Frame Detection -->
          <div v-if="qcResult" class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="snowflake" size="is-small"></b-icon>
              Freeze Frame Detection
            </h4>
            <div class="columns">
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Freeze Frames Found</p>
                  <p class="title is-4" :class="{ 'has-text-warning': qcResult.freeze_frame_count > 0 }">
                    {{ qcResult.freeze_frame_count || 0 }}
                  </p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Total Duration</p>
                  <p class="title is-4">{{ formatSeconds(qcResult.total_freeze_duration_s) }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Status</p>
                  <b-icon
                    :icon="qcResult.freeze_frame_count > 0 ? 'alert' : 'check-circle'"
                    :type="qcResult.freeze_frame_count > 0 ? 'is-warning' : 'is-success'"
                    size="is-medium"
                  ></b-icon>
                </div>
              </div>
            </div>
            <div v-if="freezeFramesList && freezeFramesList.length > 0" class="mt-4">
              <p class="subtitle is-6 mb-2">Detected Freeze Frames:</p>
              <div class="table-container" style="max-height: 200px; overflow-y: auto;">
                <table class="table is-narrow is-fullwidth is-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(frame, index) in freezeFramesList" :key="'ff-' + index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatTimecode(frame.start_s || frame.freeze_start || frame.start) }}</td>
                      <td>{{ formatTimecode(frame.end_s || frame.freeze_end || frame.end) }}</td>
                      <td>{{ formatSeconds(frame.duration_s || frame.freeze_duration || frame.duration) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Segment Detection -->
          <div v-if="qcResult" class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="puzzle" size="is-small"></b-icon>
              Segment Detection
            </h4>
            <div class="columns">
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Detected Segments</p>
                  <p class="title is-4">{{ detectedSegmentsList.length }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Expected Segments</p>
                  <p class="title is-4">{{ expectedSegmentsList.length || 'N/A' }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Match</p>
                  <b-icon
                    :icon="qcResult.segments_match ? 'check-circle' : 'close-circle'"
                    :type="qcResult.segments_match ? 'is-success' : 'is-danger'"
                    size="is-medium"
                  ></b-icon>
                </div>
              </div>
            </div>
            <div v-if="detectedSegmentsList && detectedSegmentsList.length > 0" class="mt-4">
              <p class="subtitle is-6 mb-2">Detected Segments:</p>
              <div class="table-container" style="max-height: 200px; overflow-y: auto;">
                <table class="table is-narrow is-fullwidth is-striped">
                  <thead>
                    <tr>
                      <th>Seg #</th>
                      <th>In Point</th>
                      <th>Out Point</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(seg, index) in detectedSegmentsList" :key="'seg-' + index">
                      <td>{{ seg.segment_number || (index + 1) }}</td>
                      <td>{{ formatTimecode(seg.start_s || seg.in_point || seg.start) }}</td>
                      <td>{{ formatTimecode(seg.end_s || seg.out_point || seg.end) }}</td>
                      <td>{{ formatSeconds(seg.duration_s || seg.duration) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-if="segmentComparisonList && segmentComparisonList.length > 0" class="mt-4">
              <p class="subtitle is-6 mb-2">Segment Comparison (Expected vs Detected):</p>
              <div class="table-container" style="max-height: 200px; overflow-y: auto;">
                <table class="table is-narrow is-fullwidth is-striped">
                  <thead>
                    <tr>
                      <th>Seg #</th>
                      <th>Expected Duration</th>
                      <th>Detected Duration</th>
                      <th>Difference</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(comp, index) in segmentComparisonList" :key="'comp-' + index">
                      <td>{{ comp.segment_number || (index + 1) }}</td>
                      <td>{{ formatSeconds(comp.expected_duration) }}</td>
                      <td>{{ formatSeconds(comp.detected_duration) }}</td>
                      <td :class="{ 'has-text-danger': Math.abs(comp.difference || 0) > 1 }">
                        {{ comp.difference ? (comp.difference > 0 ? '+' : '') + comp.difference.toFixed(2) + 's' : '-' }}
                      </td>
                      <td>
                        <b-icon
                          :icon="comp.match ? 'check' : 'close'"
                          :type="comp.match ? 'is-success' : 'is-danger'"
                          size="is-small"
                        ></b-icon>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- A/V Sync (Lip Sync) Analysis -->
          <div v-if="qcResult" class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="sync" size="is-small"></b-icon>
              A/V Sync (Lip Sync) Analysis
            </h4>
            <div class="columns">
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Sync Offset</p>
                  <p class="title is-4">{{ qcResult.av_sync_offset_ms != null ? qcResult.av_sync_offset_ms + ' ms' : 'N/A' }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">Max Drift</p>
                  <p class="title is-4" :class="{ 'has-text-warning': Math.abs(qcResult.av_sync_drift_ms || 0) > 40 }">
                    {{ qcResult.av_sync_drift_ms != null ? qcResult.av_sync_drift_ms + ' ms' : 'N/A' }}
                  </p>
                </div>
              </div>
              <div class="column is-4">
                <div class="has-text-centered">
                  <p class="heading">In Sync</p>
                  <b-icon
                    :icon="qcResult.av_sync_synced ? 'check-circle' : 'close-circle'"
                    :type="qcResult.av_sync_synced ? 'is-success' : 'is-danger'"
                    size="is-medium"
                  ></b-icon>
                </div>
              </div>
            </div>
            <div class="mt-3">
              <p class="has-text-grey is-size-7">
                <b-icon icon="information" size="is-small"></b-icon>
                Acceptable lip sync tolerance: ±40ms (imperceptible), ±80ms (acceptable)
              </p>
            </div>
          </div>

          <!-- Processing Info -->
          <div class="box">
            <h4 class="title is-5 mb-4">
              <b-icon icon="information" size="is-small"></b-icon>
              Processing Info
            </h4>
            <div class="columns is-multiline">
              <div class="column is-6">
                <p><strong>Created:</strong> {{ formatDateTime(qcResult.created_at) }}</p>
                <p v-if="qcResult.completed_at"><strong>Completed:</strong> {{ formatDateTime(qcResult.completed_at) }}</p>
              </div>
              <div class="column is-6">
                <p v-if="qcResult.processing_time_ms">
                  <strong>Processing Time:</strong> {{ (qcResult.processing_time_ms / 1000).toFixed(1) }}s
                </p>
                <p v-if="qcResult.worker_instance_id">
                  <strong>Worker:</strong> {{ qcResult.worker_instance_id }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot" style="justify-content: flex-end">
        <b-button @click="closeModal">Close</b-button>
        <b-button
          v-if="canOverride"
          type="is-warning"
          icon-left="shield-check"
          @click="confirmOverride"
        >
          Override QC
        </b-button>
        <b-button
          v-if="canRetry"
          type="is-info"
          icon-left="refresh"
          @click="retryJob"
        >
          Retry
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import QcApi from '@/api/qc'

export default {
  name: 'ViewQcResultModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    qcResultId: {
      type: [Number, String],
      default: null,
      required: true
    }
  },

  data() {
    return {
      showModal: this.show,
      loading: false,
      error: null,
      qcResult: null
    }
  },

  computed: {
    statusClass() {
      if (!this.qcResult) return 'is-light'
      const status = this.qcResult.qc_status
      if (status === 'passed') return 'is-success is-light'
      if (status === 'passed_with_warnings') return 'is-warning is-light'
      if (status === 'failed') return 'is-danger is-light'
      if (status === 'processing') return 'is-info is-light'
      return 'is-light'
    },

    statusIcon() {
      if (!this.qcResult) return 'help-circle'
      const status = this.qcResult.qc_status
      if (status === 'passed') return 'check-circle'
      if (status === 'passed_with_warnings') return 'alert'
      if (status === 'failed') return 'close-circle'
      if (status === 'processing') return 'loading'
      return 'help-circle'
    },

    canOverride() {
      if (!this.qcResult) return false
      return ['passed_with_warnings', 'failed'].includes(this.qcResult.qc_status)
    },

    canRetry() {
      if (!this.qcResult) return false
      return ['failed', 'error'].includes(this.qcResult.qc_status)
    },

    audioStreams() {
      const raw = this.qcResult && this.qcResult.audio_streams
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) || [] } catch (e) { return [] }
      }
      return []
    },

    audioMappingLabel() {
      if (!this.qcResult) return ''
      if (this.qcResult.audio_mapping_strategy === 'split-mono') {
        return 'Split-mono L/R merged → stereo'
      }
      return this.qcResult.audio_mapping_description
        || this.qcResult.audio_mapping_strategy
        || 'simple'
    },

    // Black frames
    hasBlackFrames() {
      return this.qcResult && (this.qcResult.black_frame_count > 0 || (this.qcResult.black_frames && this.qcResult.black_frames.length > 0))
    },
    blackFramesList() {
      if (!this.qcResult || !this.qcResult.black_frames) return []
      return Array.isArray(this.qcResult.black_frames) ? this.qcResult.black_frames : []
    },

    // Silence periods
    hasSilencePeriods() {
      return this.qcResult && (this.qcResult.silence_count > 0 || (this.qcResult.silence_periods && this.qcResult.silence_periods.length > 0))
    },
    silencePeriodsList() {
      if (!this.qcResult || !this.qcResult.silence_periods) return []
      return Array.isArray(this.qcResult.silence_periods) ? this.qcResult.silence_periods : []
    },

    // Freeze frames
    hasFreezeFrames() {
      return this.qcResult && (this.qcResult.freeze_frame_count > 0 || (this.qcResult.freeze_frames && this.qcResult.freeze_frames.length > 0))
    },
    freezeFramesList() {
      if (!this.qcResult || !this.qcResult.freeze_frames) return []
      return Array.isArray(this.qcResult.freeze_frames) ? this.qcResult.freeze_frames : []
    },

    // Segments
    hasSegmentData() {
      return this.qcResult && (
        (this.qcResult.detected_segments && this.qcResult.detected_segments.length > 0) ||
        this.qcResult.segments_match !== undefined
      )
    },
    detectedSegmentsList() {
      if (!this.qcResult || !this.qcResult.detected_segments) return []
      return Array.isArray(this.qcResult.detected_segments) ? this.qcResult.detected_segments : []
    },
    expectedSegmentsList() {
      if (!this.qcResult || !this.qcResult.expected_segments) return []
      return Array.isArray(this.qcResult.expected_segments) ? this.qcResult.expected_segments : []
    },
    segmentComparisonList() {
      if (!this.qcResult || !this.qcResult.segment_comparison) return []
      return Array.isArray(this.qcResult.segment_comparison) ? this.qcResult.segment_comparison : []
    },

    // A/V Sync
    hasAvSyncData() {
      return this.qcResult && (
        this.qcResult.av_sync_offset_ms !== undefined ||
        this.qcResult.av_sync_drift_ms !== undefined ||
        this.qcResult.av_sync_synced !== undefined
      )
    }
  },

  watch: {
    show(val) {
      this.showModal = val
      if (val && this.qcResultId) {
        this.fetchResult()
      }
    }
  },

  async created() {
    if (this.qcResultId) {
      await this.fetchResult()
    }
  },

  methods: {
    async fetchResult() {
      if (!this.qcResultId) {
        this.error = 'No QC result ID provided'
        return
      }

      this.loading = true
      this.error = null

      try {
        this.qcResult = await QcApi.getResult(this.qcResultId)
      } catch (e) {
        console.error('Failed to load QC result:', e)
        this.error = e.message || 'Failed to load QC result'
      } finally {
        this.loading = false
      }
    },

    formatStatus(status) {
      const statusMap = {
        pending: 'Pending',
        queued: 'Queued',
        processing: 'Processing',
        passed: 'Passed',
        passed_with_warnings: 'Passed with Warnings',
        failed: 'Failed',
        error: 'Error',
        fixed: 'Fixed'
      }
      return statusMap[status] || status
    },

    formatDuration(ms) {
      if (!ms) return 'N/A'
      const seconds = Math.floor(ms / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      const remainingSeconds = seconds % 60

      if (hours > 0) {
        return `${hours}:${String(remainingMinutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
      }
      return `${remainingMinutes}:${String(remainingSeconds).padStart(2, '0')}`
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString()
    },

    severityClass(severity) {
      const classes = {
        error: 'is-danger',
        warning: 'is-warning',
        info: 'is-info'
      }
      return classes[severity] || 'is-light'
    },

    formatSeconds(seconds) {
      if (seconds === null || seconds === undefined) return 'N/A'
      const s = parseFloat(seconds)
      if (isNaN(s)) return 'N/A'
      if (s < 60) return s.toFixed(2) + 's'
      const mins = Math.floor(s / 60)
      const secs = (s % 60).toFixed(2)
      return `${mins}m ${secs}s`
    },

    formatTimecode(seconds) {
      if (seconds === null || seconds === undefined) return 'N/A'
      const s = parseFloat(seconds)
      if (isNaN(s)) return 'N/A'
      const hours = Math.floor(s / 3600)
      const mins = Math.floor((s % 3600) / 60)
      const secs = Math.floor(s % 60)
      const frames = Math.round((s % 1) * 25) // Assuming 25fps
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`
    },

    downloadReport() {
      const url = QcApi.getReportUrl(this.qcResultId)
      window.open(url, '_blank')
    },

    confirmOverride() {
      this.$buefy.dialog.prompt({
        message: 'Enter the reason for overriding this QC result:',
        inputAttrs: {
          placeholder: 'Minimum 10 characters',
          minlength: 10
        },
        trapFocus: true,
        onConfirm: async (reason) => {
          if (!reason || reason.length < 10) {
            this.$buefy.toast.open({
              message: 'Reason must be at least 10 characters',
              type: 'is-danger'
            })
            return
          }

          try {
            await QcApi.override(this.qcResultId, reason)
            this.$buefy.toast.open({
              message: 'QC result overridden successfully',
              type: 'is-success'
            })
            this.$emit('refresh')
            await this.fetchResult()
          } catch (e) {
            this.$buefy.toast.open({
              message: 'Failed to override: ' + (e.message || 'Unknown error'),
              type: 'is-danger'
            })
          }
        }
      })
    },

    async retryJob() {
      try {
        await QcApi.retry(this.qcResultId)
        this.$buefy.toast.open({
          message: 'QC job queued for retry',
          type: 'is-success'
        })
        this.$emit('refresh')
        await this.fetchResult()
      } catch (e) {
        this.$buefy.toast.open({
          message: 'Failed to retry: ' + (e.message || 'Unknown error'),
          type: 'is-danger'
        })
      }
    },

    closeModal() {
      this.showModal = false
      this.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
.box {
  margin-bottom: 1rem;
}

.table td {
  vertical-align: middle;
}

.tag {
  text-transform: capitalize;
}
</style>
